import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { setAuthState, clearAuthState } from "./authSlice";

type MilestonePayload = {
  title: string;
  description: string;
  date: string;
  completed: boolean;
};

type GoalPayload = {
  title: string;
  description: string;
  completed: boolean;
  milestones: MilestonePayload[];
};

type DreamPayload = {
  dream: {
    title: string;
    description: string;
    startMonth: number;
    startYear: number;
    targetMonth: number;
    targetYear: number;
    color: string;
    category: string;
  };
  goals: GoalPayload[];
};

type CreateDreamArgs = {
  payload: DreamPayload;
  getAccessToken: () => Promise<string>;
};

type ToggleDreamCompletedArgs = {
  dreamId: string;
  completed: boolean;
  getAccessToken: () => Promise<string>;
};

type DreamState = {
  ownerId: string | null;
  pendingPayload: (DreamPayload & { requestId: string }) | null;
  createRequestId: string | null;
  createdDream: Record<string, unknown> | null;
  isCreating: boolean;
  createError: string | null;
  isTogglingCompleted: boolean;
  toggleCompletedError: string | null;
};

const initialState: DreamState = {
  ownerId: null,
  pendingPayload: null,
  createRequestId: null,
  createdDream: null,
  isCreating: false,
  createError: null,
  isTogglingCompleted: false,
  toggleCompletedError: null,
};

export const createDream = createAsyncThunk(
  "dream/createDream",
  async ({ getAccessToken }: CreateDreamArgs, { rejectWithValue, getState, dispatch }) => {
    const current = (getState() as { dream: DreamState }).dream;
    const ownerId = current.ownerId;
    try {
      const result = await request(API_URLS.dreamsFullCreate, {
        method: "POST",
        auth: true,
        getAccessToken,
        body: current.pendingPayload,
      });
      if ((getState() as { dream: DreamState }).dream.ownerId !== ownerId) throw new Error("Your session changed.");
      return result;
    } catch (error: unknown) {
      if (error && typeof error === "object" && "httpStatus" in error && [400, 422].includes(Number(error.httpStatus))) dispatch(clearPendingSave());
      const message =
        error instanceof Error ? error.message : "Failed to create dream";
      return rejectWithValue(message);
    }
  },
  { condition: (_args, { getState }) => !(getState() as { dream: DreamState }).dream.isCreating },
);

export const toggleDreamCompleted = createAsyncThunk(
  "dream/toggleDreamCompleted",
  async (
    { dreamId, completed, getAccessToken }: ToggleDreamCompletedArgs,
    { rejectWithValue },
  ) => {
    try {
      const result = await request(API_URLS.dream(dreamId), {
        method: "PUT",
        body: { completed },
        auth: true,
        getAccessToken,
      });
      return result;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update dream";
      return rejectWithValue(errorMessage);
    }
  },
);

const dreamSlice = createSlice({
  name: "dream",
  initialState,
  reducers: {
    clearPendingSave: (state) => { state.pendingPayload = null; },
    resetCreateDreamState: (state) => {
      state.pendingPayload = null;
      state.createRequestId = null;
      state.createdDream = null;
      state.isCreating = false;
      state.createError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearAuthState, () => initialState)
      .addCase(setAuthState, (state, action) => {
        const ownerId = action.payload?.user?.sub ?? null;
        if (state.ownerId !== ownerId) return { ...initialState, ownerId };
      })
      .addCase(createDream.pending, (state, action) => {
        state.createRequestId = action.meta.requestId;
        if (!state.pendingPayload) state.pendingPayload = { ...JSON.parse(JSON.stringify(action.meta.arg.payload)), requestId: action.meta.requestId };
        state.isCreating = true;
        state.createError = null;
        state.createdDream = null;
      })
      .addCase(createDream.fulfilled, (state, action) => {
        if (state.createRequestId !== action.meta.requestId) return;
        state.isCreating = false;
        state.createdDream = action.payload;
      })
      .addCase(createDream.rejected, (state, action) => {
        if (state.createRequestId !== action.meta.requestId) return;
        state.isCreating = false;
        state.createError = (action.payload as string) || "Failed to create dream";
      })
      .addCase(toggleDreamCompleted.pending, (state) => {
        state.isTogglingCompleted = true;
        state.toggleCompletedError = null;
      })
      .addCase(toggleDreamCompleted.fulfilled, (state) => {
        state.isTogglingCompleted = false;
      })
      .addCase(toggleDreamCompleted.rejected, (state, action) => {
        state.isTogglingCompleted = false;
        state.toggleCompletedError =
          (action.payload as string) || "Failed to update dream";
      });
  },
});

export const { resetCreateDreamState, clearPendingSave } = dreamSlice.actions;
export default dreamSlice.reducer;
